// Auswertung des Modellvergleichs in der Admin-Übersicht (seit 24.10.2026).
// Die Läufe macht der Server (?resource=ki-modell, api/_lib/modellVergleich.js),
// je Anfrage ein Fall mit einem Modell. Hier: Reihenfolge, Zusammenfassung,
// Empfehlung und der Text zum Kopieren.

export const STANDARD = 'bitrix/bitrixgpt-5.5';
export const DENKMODELL = 'bitrix/bitrixgpt-5.5-thinking';

export function modellName(modell) {
  if (modell === STANDARD) return 'Standard';
  if (modell === DENKMODELL) return 'Denkmodell';
  return String(modell || '').split('/').pop() || '–';
}

/** Runde für Runde, je Fall beide Modelle - abwechselnd, damit Last und Tageszeit beide gleich treffen. */
export function vergleichsPlan(faelle = [], modelle = [], runden = 1) {
  const plan = [];
  for (let runde = 1; runde <= runden; runde += 1) {
    for (const fall of faelle) {
      const reihe = runde % 2 ? modelle : [...modelle].reverse();
      reihe.forEach((modell) => plan.push({ runde, fall: fall.name, modell }));
    }
  }
  return plan;
}

const schnitt = (werte) => (werte.length ? werte.reduce((s, w) => s + w, 0) / werte.length : 0);

/** Je Modell: Läufe, Fehler (nach Art), Ø/max Sekunden, Ø Token, erfüllte Prüfungen. */
export function zusammenfassung(ergebnisse = [], modelle = []) {
  return modelle.map((modell) => {
    const eigene = ergebnisse.filter((e) => e.modell === modell);
    const gut = eigene.filter((e) => !e.fehler);
    const pruefungen = gut.flatMap((e) => (e.pruefungen || []).filter((p) => !p.info));
    const fehlerArten = {};
    eigene.filter((e) => e.fehler).forEach((e) => { fehlerArten[e.fehler] = (fehlerArten[e.fehler] || 0) + 1; });
    return {
      modell,
      laeufe: eigene.length,
      fehler: eigene.length - gut.length,
      fehlerArten,
      schnittS: schnitt(gut.map((e) => e.dauerS || 0)),
      maxS: gut.length ? Math.max(...gut.map((e) => e.dauerS || 0)) : 0,
      schnittTokenAus: Math.round(schnitt(gut.map((e) => e.tokenAus || 0))),
      erfuellt: pruefungen.filter((p) => p.ok).length,
      geprueft: pruefungen.length,
    };
  });
}

/** Je Fall und Prüfung: erfüllt / Läufe ohne Fehler, je Modell. */
export function pruefungsTabelle(faelle = [], ergebnisse = [], modelle = []) {
  return faelle.map((fall) => ({
    fall: fall.name,
    beschreibung: fall.beschreibung,
    zeilen: (fall.pruefungen || []).map((p) => ({
      name: p.name,
      info: Boolean(p.info),
      je: modelle.map((modell) => {
        const treffer = ergebnisse.filter((e) => e.modell === modell && e.fall === fall.name && !e.fehler)
          .map((e) => (e.pruefungen || []).find((x) => x.name === p.name)?.ok);
        return { modell, erfuellt: treffer.filter(Boolean).length, von: treffer.length };
      }),
    })),
  }));
}

// Über 120 s je Fall wird es eng: Die Gesprächsrunde hat 300 s, danach kommen
// noch Einordnung und Speichern.
const ZU_LANGSAM_S = 120;

/**
 * Eine vorsichtige Empfehlung - entscheiden muss der Mensch nach dem Lesen der Texte.
 * @returns {{ ton: 'gut' | 'warn', text: string } | null}
 */
export function empfehlung(zusammen = []) {
  const standard = zusammen.find((z) => z.modell === STANDARD);
  const denk = zusammen.find((z) => z.modell === DENKMODELL);
  if (!standard || !denk || !standard.laeufe || !denk.laeufe) return null;
  const quote = (z) => (z.geprueft ? z.erfuellt / z.geprueft : 0);
  if (denk.fehler > 0) {
    return { ton: 'warn', text: `Beim Standard bleiben: Das Denkmodell ist ${denk.fehler}× abgebrochen (${Object.keys(denk.fehlerArten).join(', ')}).` };
  }
  if (denk.maxS > ZU_LANGSAM_S) {
    return { ton: 'warn', text: `Beim Standard bleiben: Das Denkmodell brauchte bis zu ${Math.round(denk.maxS)} s für einen Fall – im Gespräch wird das zu knapp.` };
  }
  if (quote(denk) > quote(standard)) {
    return { ton: 'gut', text: `Das Denkmodell erfüllt mehr Prüfungen (${denk.erfuellt}/${denk.geprueft} gegen ${standard.erfuellt}/${standard.geprueft}) ohne Abbruch. Texte unten vergleichen – dann umschalten.` };
  }
  return { ton: 'warn', text: `Kein messbarer Vorteil des Denkmodells (${denk.erfuellt}/${denk.geprueft} gegen ${standard.erfuellt}/${standard.geprueft}). Texte unten vergleichen; im Zweifel beim Standard bleiben.` };
}

/** Alles als Text - zum Kopieren und Weitergeben. */
export function ergebnisAlsText({ faelle = [], ergebnisse = [], modelle = [] } = {}) {
  const zeilen = ['Modellvergleich „KI im Gespräch“', ''];
  zusammenfassung(ergebnisse, modelle).forEach((z) => {
    const fehler = Object.entries(z.fehlerArten).map(([art, n]) => `${art} ×${n}`).join(', ');
    zeilen.push(`${modellName(z.modell)} (${z.modell}): ${z.laeufe} Läufe, ${z.fehler} Fehler${fehler ? ` (${fehler})` : ''}, `
      + `Ø ${z.schnittS.toFixed(1)} s, max ${Math.round(z.maxS)} s, Ø ${z.schnittTokenAus} Token, Prüfungen ${z.erfuellt}/${z.geprueft}`);
  });
  zeilen.push('');
  pruefungsTabelle(faelle, ergebnisse, modelle).forEach((f) => {
    zeilen.push(`${f.fall} – ${f.beschreibung}`);
    f.zeilen.forEach((z) => zeilen.push(`  ${z.info ? '(Info) ' : ''}${z.name}: ${z.je.map((j) => `${modellName(j.modell)} ${j.erfuellt}/${j.von}`).join(' · ')}`));
  });
  zeilen.push('', 'Texte (erste Runde ohne Fehler):');
  faelle.forEach((f) => {
    modelle.forEach((modell) => {
      const e = ergebnisse.find((x) => x.fall === f.name && x.modell === modell && !x.fehler);
      if (!e) return;
      zeilen.push(`--- ${f.name} · ${modellName(modell)} ---`);
      (e.antworten || []).forEach((a) => zeilen.push(`KI: ${a}`));
      (e.ersetzungen || []).forEach((a) => zeilen.push(`Ersetzung: „${a.alt}“ → „${a.neu || '(streichen)'}“`));
      (e.ergaenzungen || []).forEach((p) => zeilen.push(`Ergänzung ${p.titel}: ${p.text}`));
    });
  });
  return zeilen.join('\n');
}
